import React, { useState, useCallback } from 'react';
import { sdkRequest } from '@eventbrite/legacy-infra.http';
import { track } from '@eventbrite/legacy-infra.datalayer-library';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { logEvent } from '@eventbrite/legacy-infra.statsig';
import { redirectToLoginWithCustomReferrer } from '@/utils/utils';
import { useStage } from '@/hooks/useStage';
import { stageFromString } from '@eventbrite/webapp-constants';
import { GACodes, LISTING_HEAP_IDS, STATSIG_EVENT_NAMES } from '@/components/_shared/constants';
import type { BookmarkAction } from '../types';
import { LIKE_ACTION, UNLIKE_ACTION } from '../types';
import { isUnauthorizedError } from './utils';
import { useToast } from '@/components/_shared/contexts/ToastContext';
import { EventLikedToast, ToastType } from '@/components/_shared/Toast';

export type BookmarkActionResponse = {
  created?: boolean;
  deleted?: boolean;
};

export function trackBookmarkActionSuccess(response: BookmarkActionResponse, eventId: string): void {
  if (response?.created === true) {
    logEvent(STATSIG_EVENT_NAMES.LISTING_ALL_SUCCESS_LIKE_EVENT);
    track({
      eventName: LISTING_HEAP_IDS.LIKE_CLICK,
      eventData: { eventId },
    });
  }
  if (response?.deleted === true) {
    track({
      eventName: LISTING_HEAP_IDS.DISLIKE_EVENT_CLICK,
      eventData: { eventId },
    });
  }
}

export interface UseBookmarkActionsResult {
  likeEvent: () => Promise<void>;
  unlikeEvent: () => Promise<void>;
  bookmarkActionLoading: boolean;
}

export const useBookmarkActions = (
  eventId: string,
  setBookmarkStatus: React.Dispatch<React.SetStateAction<boolean>>,
): UseBookmarkActionsResult => {
  const [loading, setLoading] = useState(false);
  const stage = useStage();
  const { showToast } = useToast();

  const redirectToLogin = useCallback(() => {
    const stageValue = stageFromString(stage);
    const referrerURL = new URL(window.location.href);
    referrerURL.searchParams.set('bookmarkEvent', eventId);

    const pathWithQuery = referrerURL.pathname + referrerURL.search;
    redirectToLoginWithCustomReferrer(pathWithQuery, stageValue);
  }, [eventId, stage]);

  const updateBookmarkState = useCallback(
    (isLiked: boolean) => {
      setBookmarkStatus(isLiked);
    },
    [setBookmarkStatus],
  );

  const performBookmarkAction = useCallback(
    async (action: BookmarkAction): Promise<void> => {
      if (!eventId) {
        console.error('useBookmarkActions: Event ID is required');
        return;
      }

      const analyticsAction =
        action === LIKE_ACTION ? GACodes.GA_ACTION_BOOKMARK_ADD : GACodes.GA_ACTION_BOOKMARK_REMOVE;
      trackAnalyticsEvent({
        action: analyticsAction,
        category: GACodes.GA_CATEGORY_LISTING,
      });

      setLoading(true);

      try {
        const endpoint = `/api/v3/users/me/bookmarks/${action}/`;
        const response = (await sdkRequest(
          endpoint,
          {
            method: 'POST',
            body: JSON.stringify({ event_id: eventId }),
          },
          process.env.NEXT_PUBLIC_PROXY_API_V3 ? '/e' : '',
        )) as BookmarkActionResponse;

        // Update the state based on the action
        const newIsLiked = action === LIKE_ACTION;
        updateBookmarkState(newIsLiked);

        if (action === LIKE_ACTION && response?.created === true) {
          showToast({
            type: ToastType.INFO,
            duration: 5000,
            content: React.createElement(EventLikedToast),
          });
        }

        trackBookmarkActionSuccess(response, eventId);
      } catch (error) {
        if (isUnauthorizedError(error)) {
          // User is not authenticated, redirect to login
          redirectToLogin();
          return;
        }

        console.error(`useBookmarkActions: Error performing ${action} action`, error);
      } finally {
        setLoading(false);
      }
    },
    [eventId, updateBookmarkState, redirectToLogin, showToast],
  );

  const likeEvent = useCallback(() => performBookmarkAction(LIKE_ACTION), [performBookmarkAction]);
  const unlikeEvent = useCallback(() => performBookmarkAction(UNLIKE_ACTION), [performBookmarkAction]);

  return {
    likeEvent,
    unlikeEvent,
    bookmarkActionLoading: loading,
  };
};
